/**
 * UploadStatusToast
 * Fixed-position corner card shown on all pages except /models.
 * Displays active/queued/finished uploads and auto-dismisses once
 * every item has reached a terminal state (complete or error).
 */

import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { X, CheckCircle, AlertCircle, Upload } from 'lucide-react';
import { useUploadContext, UploadQueueItem, UploadItemStatus } from '../../contexts/UploadContext';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const statusColour: Record<UploadItemStatus, string> = {
  queued:     'text-gray-500',
  uploading:  'text-blue-600',
  processing: 'text-amber-600',
  complete:   'text-emerald-600',
  error:      'text-red-600',
};

function statusLabel(item: UploadQueueItem): string {
  switch (item.status) {
    case 'queued':     return 'Queued';
    case 'uploading':  return `Uploading ${item.progress}%`;
    case 'processing': return 'Processing…';
    case 'complete':   return 'Complete';
    case 'error':      return 'Failed';
  }
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

interface ToastRowProps {
  item: UploadQueueItem;
}

const ToastRow: React.FC<ToastRowProps> = ({ item }) => (
  <li className="py-1.5 first:pt-0 last:pb-0">
    <div className="flex items-center gap-2">
      {/* Terminal-state icons */}
      {item.status === 'complete' && (
        <CheckCircle className="h-4 w-4 flex-shrink-0 text-emerald-500" aria-hidden="true" />
      )}
      {item.status === 'error' && (
        <AlertCircle className="h-4 w-4 flex-shrink-0 text-red-500" aria-hidden="true" />
      )}
      {(item.status === 'queued' || item.status === 'uploading' || item.status === 'processing') && (
        <Upload className="h-4 w-4 flex-shrink-0 text-gray-400" aria-hidden="true" />
      )}

      {/* Filename + status */}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-gray-800" title={item.name}>
          {item.name}
        </p>
        <p className={`text-xs ${statusColour[item.status]}`}>
          {statusLabel(item)}
        </p>
        {/* Error detail */}
        {item.status === 'error' && item.error && (
          <p className="truncate text-xs text-red-500" title={item.error}>
            {item.error}
          </p>
        )}
      </div>
    </div>

    {/* Progress bar — only whilst uploading */}
    {item.status === 'uploading' && (
      <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-gray-100" aria-hidden="true">
        <div
          className="h-full rounded-full bg-blue-500 transition-[width] duration-200"
          style={{ width: `${item.progress}%` }}
        />
      </div>
    )}
  </li>
);

// ---------------------------------------------------------------------------
// Toast
// ---------------------------------------------------------------------------

const UploadStatusToast: React.FC = () => {
  const { queue, dismissCompleted } = useUploadContext();
  const location = useLocation();

  const isOnModelsPage = location.pathname === '/models';

  /** Auto-dismiss the toast 5 seconds after all items reach a terminal state */
  useEffect(() => {
    const allDone =
      queue.length > 0 &&
      queue.every(i => i.status === 'complete' || i.status === 'error');

    if (!allDone) return;

    const timer = setTimeout(() => dismissCompleted(), 5000);
    return () => clearTimeout(timer);
  }, [queue, dismissCompleted]);

  if (isOnModelsPage || queue.length === 0) return null;

  const hasFinished = queue.some(i => i.status === 'complete' || i.status === 'error');

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="Upload status"
      className="fixed bottom-6 right-6 z-[55] max-w-xs w-full rounded-xl border border-gray-200 bg-white p-3 shadow-lg"
    >
      {/* Header */}
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Uploads
        </span>
        {hasFinished && (
          <button
            type="button"
            onClick={dismissCompleted}
            title="Dismiss completed"
            aria-label="Dismiss completed uploads"
            className="rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </div>

      {/* Items */}
      <ul className="divide-y divide-gray-100">
        {queue.map(item => (
          <ToastRow key={item.id} item={item} />
        ))}
      </ul>
    </div>
  );
};

export default UploadStatusToast;
